import { useState, type CSSProperties, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { formatDate } from "../../api/dates";
import {
  clock,
  dayColor,
  fmtDuration,
  fmtKm,
  useCompleteDay,
  useReassignDay,
  useStopOutcome,
  useUploadVisitSheet,
  type PlanDetail,
  type PlanStop,
  type VisitRef,
} from "../../api/routing";
import { StatusChip, type Tone } from "../../components/ui";
import AddStop from "./AddStop";
import ChangeStop from "./ChangeStop";
import StopActual from "./StopActual";
import { canEditStop, SwapTrail } from "./SwapStop";

const OUTCOME_LABEL: Record<string, string> = { pending: "Pending", done: "Done", not_done: "Not done", cancelled: "Taken off" };
const OUTCOME_TONE: Record<string, Tone> = { pending: "quiet", done: "ok", not_done: "warn", cancelled: "quiet" };

/** One day of a saved plan: its crew and date, the stops with what became of
 * each, and closing the day or handing it to another crew. */
export default function PlanDay({ detail, dayIndex }: { detail: PlanDetail; dayIndex: number }) {
  const day = detail.days[dayIndex];
  const plan = detail.plan;
  const completeDay = useCompleteDay(plan.id);
  const reassign = useReassignDay(plan.id);
  const [crew, setCrew] = useState("");
  const [adding, setAdding] = useState(false);
  const done = day.completed_at !== null;
  const finished = day.stops.filter((s) => s.outcome === "done").length;
  return (
    <section className="card plan-day" style={{ "--day": dayColor(day.day_index) } as CSSProperties} aria-label={`Day ${day.day_index + 1}`}>
      <header className="plan-day-head">
        <strong>
          Day {day.day_index + 1}
          {day.date ? ` · ${formatDate(day.date)}` : ""}
        </strong>
        <span className="plan-day-meta">
          {day.provider?.name ?? "No field operators yet"} · {day.stops.length} stops · {fmtKm(day.total_m)} km · {fmtDuration(day.total_s)}
        </span>
        {day.day_type === "weekend" ? <StatusChip tone="warn">Weekend</StatusChip> : null}
        {done ? <StatusChip tone="ok">Day complete</StatusChip> : null}
        <span className="plan-day-progress">
          {finished} of {day.stops.length} done
        </span>
      </header>
      {day.stops.map((stop) => (
        <StopRow key={stop.id} detail={detail} stop={stop} dayIndex={day.day_index} dayProvider={day.provider} />
      ))}
      {adding && !done ? <AddStop planId={plan.id} day={day} onDone={() => setAdding(false)} /> : null}
      {plan.editable ? (
        <div className="plan-day-foot">
          <button type="button" className="btn" disabled={completeDay.isPending} onClick={() => completeDay.mutate({ dayIndex: day.day_index, reopen: done })}>
            {done ? "Reopen the day" : "Mark the day complete"}
          </button>
          {!done ? (
            <button type="button" className="btn btn-secondary" aria-expanded={adding} onClick={() => setAdding((a) => !a)}>
              {adding ? "Close" : "+ Add a site to this day"}
            </button>
          ) : null}
          <label className="field">
            Hand the day to
            <select value={crew} onChange={(e) => setCrew(e.target.value)}>
              <option value="">Choose the field operators…</option>
              {detail.providers
                .filter((p) => p.id !== day.provider?.id)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </select>
          </label>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={reassign.isPending || crew === ""}
            onClick={() => reassign.mutate({ dayIndex: day.day_index, provider_id: Number(crew) }, { onSuccess: () => setCrew("") })}
          >
            Move the day and its sites
          </button>
          {completeDay.isError ? <p className="notice bad">{completeDay.error.message}</p> : null}
          {reassign.isError ? <p className="notice bad">{reassign.error.message}</p> : null}
        </div>
      ) : null}
    </section>
  );
}

/** "Week 40 · Day 2 · 2 Oct 2026": another visit to the same site, as a stop
 * row names it. */
function visitLabel(visit: VisitRef): string {
  return [visit.plan_name, `Day ${visit.day_index + 1}`, visit.date ? formatDate(visit.date) : null].filter(Boolean).join(" · ");
}

/** A stop: tap Pending or Done and it is recorded; Not done needs a reason,
 * so it opens the note, where the site can also be handed to another crew.
 * A stop not done is a visit still owed: the site stays on the planner's
 * "Visit not done" list until it is planned again.
 *
 * The note is also where the crew's job card is filed -- a PDF, or photos of
 * it or of the work, several at once -- uploaded with Save. Each is kept on the
 * SITE, under its documents, and linked on the stop row here. */
/** The works order (the City's notification number) and the RS number, as
 * the Sites register shows them; nothing when the site has neither. */
function SiteNumbers({ notifNo, rsNo }: { notifNo: string | null; rsNo: string | null }) {
  if (notifNo === null && rsNo === null) {
    return null;
  }
  return (
    <span className="plan-stop-numbers">
      {notifNo !== null ? (
        <>
          Works order <span className="mono">{notifNo}</span>
        </>
      ) : null}
      {notifNo !== null && rsNo !== null ? " · " : null}
      {rsNo !== null ? <span className="mono">{rsNo}</span> : null}
    </span>
  );
}

function StopRow({
  detail,
  stop,
  dayIndex,
  dayProvider,
}: {
  detail: PlanDetail;
  stop: PlanStop;
  dayIndex: number;
  dayProvider: { id: number; name: string } | null;
}) {
  const record = useStopOutcome(detail.plan.id);
  const upload = useUploadVisitSheet(detail.plan.id);
  const [open, setOpen] = useState(false);
  const [sheets, setSheets] = useState<File[]>([]);
  // Bumped to clear the file input once its files are filed.
  const [sheetInput, setSheetInput] = useState(0);
  const [outcome, setOutcome] = useState(stop.outcome);
  const [note, setNote] = useState(stop.outcome_note ?? "");
  const [reason, setReason] = useState(stop.outcome_reason ?? "");
  const [reassignTo, setReassignTo] = useState("");
  const [changing, setChanging] = useState(false);
  // Taken off the plan: kept as the record, with no number and no buttons.
  const cancelled = stop.outcome === "cancelled";
  // Recorded before the reasons list: a note and no reason. Editing it must not
  // force a category nobody chose at the time.
  const noteOnly = stop.outcome === "not_done" && stop.outcome_reason === null && stop.outcome_note !== null;
  const moved = stop.assigned !== null && dayProvider !== null && stop.assigned.id !== dayProvider.id;
  // A swapped-out stop shows its outcome read-only: no outcome buttons, Note or Swap.
  const editable = canEditStop(detail.plan.editable, stop);

  function quick(next: PlanStop["outcome"]) {
    if (next === "not_done") {
      setOutcome("not_done");
      setOpen(true);
      return;
    }
    record.mutate({ stopId: stop.id, outcome: next, note: stop.outcome_note, reason: stop.outcome_reason, reassign_to: null });
  }
  function submit(event: FormEvent) {
    event.preventDefault();
    record.mutate(
      {
        stopId: stop.id,
        outcome,
        note: note.trim() || null,
        reason: outcome === "not_done" ? reason || null : null,
        reassign_to: reassignTo === "" ? null : Number(reassignTo),
      },
      {
        onSuccess: () => {
          if (sheets.length === 0) {
            setOpen(false);
            return;
          }
          // The outcome is saved first, then each file in turn. One that is
          // refused stops there and leaves the form open with the reason; the
          // outcome stands, and a file already filed is not filed twice if Save
          // is pressed again (the same bytes on a site are one document).
          void fileAll(sheets).then(
            () => {
              setSheets([]);
              setSheetInput((n) => n + 1);
              setOpen(false);
            },
            () => undefined,
          );
        },
      },
    );
  }
  async function fileAll(files: File[]) {
    for (const file of files) {
      await upload.mutateAsync({ stopId: stop.id, file });
    }
  }
  const saving = record.isPending || upload.isPending;

  return (
    <>
      <div className={cancelled ? "plan-stop cancelled" : stop.outcome === "not_done" ? "plan-stop not-done" : "plan-stop"}>
        <span className="plan-stop-seq">{cancelled ? "—" : stop.seq}</span>
        <span className="plan-stop-site">
          {stop.site ? <Link to={`/sites/${stop.site.id}`}>{stop.site.name}</Link> : "—"}
          {stop.site ? <SiteNumbers notifNo={stop.site.notif_no} rsNo={stop.site.rs_no} /> : null}
          <StopActual stop={stop} />
          {stop.outcome_reason_label || stop.outcome_note ? (
            <span className="plan-stop-note">{[stop.outcome_reason_label, stop.outcome_note].filter(Boolean).join(" — ")}</span>
          ) : null}
          {stop.documents.length > 0 ? (
            <span className="plan-stop-docs">
              {stop.documents.map((document) => (
                <a key={document.id} href={document.url} target="_blank" rel="noreferrer">
                  {document.label}: {document.filename}
                </a>
              ))}
            </span>
          ) : null}
          {stop.replaced_by ? (
            <span className="plan-stop-trail">
              <Link to={`/routing/plans/${stop.replaced_by.plan_id}`}>Planned again: {visitLabel(stop.replaced_by)}</Link>
            </span>
          ) : null}
          {stop.replaces ? (
            <span className="plan-stop-trail">
              <Link to={`/routing/plans/${stop.replaces.plan_id}`}>Repeats the visit of {visitLabel(stop.replaces)}</Link>
              {stop.replaces.note ? `: ${stop.replaces.note}` : ""}
            </span>
          ) : null}
          <SwapTrail stop={stop} />
          {stop.change_note ? <span className="plan-stop-trail">{stop.change_note}</span> : null}
          <span className="plan-stop-with">
            Site now with {stop.assigned?.name ?? "nobody"}
            {moved ? (
              <>
                {" "}
                <StatusChip tone="warn">moved</StatusChip>
              </>
            ) : null}
          </span>
        </span>
        <span className="plan-stop-time">{clock(stop.arrive_min)}</span>
        <span className="plan-stop-tools">
          {editable ? (
            <>
              <div className="segmented" role="group" aria-label={`Outcome for stop ${stop.seq}`}>
                {detail.outcomes.map((o) => (
                  <button
                    key={o}
                    type="button"
                    className={`tone-${o}`}
                    aria-pressed={stop.outcome === o}
                    disabled={record.isPending}
                    onClick={() => quick(o as PlanStop["outcome"])}
                  >
                    {OUTCOME_LABEL[o] ?? o}
                  </button>
                ))}
              </div>
              <button type="button" className="row-button" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
                {open ? "Close" : "Note"}
              </button>
              {stop.outcome === "pending" ? (
                <button type="button" className="row-button" aria-expanded={changing} onClick={() => setChanging((c) => !c)}>
                  {changing ? "Keep it" : "Change…"}
                </button>
              ) : null}
            </>
          ) : (
            <StatusChip tone={OUTCOME_TONE[stop.outcome]}>{OUTCOME_LABEL[stop.outcome]}</StatusChip>
          )}
        </span>
      </div>
      {record.isError && !open ? <p className="notice bad">{record.error.message}</p> : null}
      {changing ? <ChangeStop detail={detail} stop={stop} dayIndex={dayIndex} onDone={() => setChanging(false)} /> : null}
      {open ? (
        <form className="plan-stop-form" onSubmit={submit} aria-label={`Record stop ${stop.seq}`}>
          <label className="field">
            Outcome
            <select value={outcome} onChange={(e) => setOutcome(e.target.value as PlanStop["outcome"])}>
              {detail.outcomes.map((o) => (
                <option key={o} value={o}>
                  {OUTCOME_LABEL[o] ?? o}
                </option>
              ))}
            </select>
          </label>
          {outcome === "not_done" ? (
            <label className="field">
              Reason
              <select value={reason} onChange={(e) => setReason(e.target.value)} required={!noteOnly}>
                <option value="">Choose why…</option>
                {detail.reasons.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <label className="field">
            Note {outcome === "not_done" && reason === "other" ? "(required)" : ""}
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={outcome === "not_done" ? "What happened, in a few words" : "What was found"}
              required={outcome === "not_done" && reason === "other"}
            />
          </label>
          <label className="field">
            Job Card or Photos
            <input
              key={sheetInput}
              type="file"
              accept="application/pdf,.pdf,image/jpeg,image/png,image/webp"
              multiple
              onChange={(e) => setSheets(Array.from(e.target.files ?? []))}
            />
          </label>
          <label className="field">
            Hand the site to
            <select value={reassignTo} onChange={(e) => setReassignTo(e.target.value)}>
              <option value="">Leave it where it is</option>
              {detail.providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {upload.isPending ? "Uploading…" : "Save"}
          </button>
          {record.isError ? <p className="notice bad">{record.error.message}</p> : null}
          {upload.isError ? <p className="notice bad">{upload.error.message}</p> : null}
        </form>
      ) : null}
    </>
  );
}
